/**
 * ImageUploadDemo - Demo page for the ProImageUploader component
 * Shows all features: multi-upload, compression, drag-reorder, watermark
 */

import React, { useState } from 'react';
import { ProImageUploader } from '../../components/ui/ProImageUploader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { toast } from 'sonner';
import { Image, Settings, Upload, CheckCircle, Info } from 'lucide-react';

export const ImageUploadDemo = () => {
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [settings, setSettings] = useState({
    watermarkEnabled: true,
    watermarkText: 'Raj Aluminium',
    watermarkContact: '085128 53955',
    watermarkPosition: 'bottom-right',
    watermarkOpacity: 40,
    maxFiles: 20,
  });

  const handleUploadComplete = (urls) => {
    setUploadedUrls(urls);
    toast.success(`${urls.length} images uploaded successfully!`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pro Image Uploader</h1>
          <p className="text-slate-500 mt-1">
            Multi-image upload with compression, watermarking, and drag-to-reorder
          </p>
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Upload, label: 'Multi Upload', desc: 'Up to 20 images' },
          { icon: Image, label: 'Auto Compress', desc: 'Target ~800KB' },
          { icon: Settings, label: 'Watermark', desc: 'Custom text' },
          { icon: CheckCircle, label: 'Retry Logic', desc: '3 auto retries' },
        ].map((feature, i) => (
          <Card key={i} className="bg-slate-50">
            <CardContent className="p-4 flex items-center gap-3">
              <feature.icon className="w-8 h-8 text-sky-500" />
              <div>
                <p className="font-medium text-slate-900">{feature.label}</p>
                <p className="text-xs text-slate-500">{feature.desc}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Watermark Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable Watermark */}
            <div className="flex items-center justify-between">
              <Label>Enable Watermark</Label>
              <Switch
                checked={settings.watermarkEnabled}
                onCheckedChange={(checked) => 
                  setSettings(s => ({ ...s, watermarkEnabled: checked }))
                }
              />
            </div>

            {settings.watermarkEnabled && (
              <>
                {/* Watermark Text */}
                <div className="space-y-2">
                  <Label>Watermark Text</Label>
                  <Input
                    value={settings.watermarkText}
                    onChange={(e) => 
                      setSettings(s => ({ ...s, watermarkText: e.target.value }))
                    }
                    placeholder="Enter watermark text"
                  />
                </div>

                {/* Contact Number */}
                <div className="space-y-2">
                  <Label>Contact Number</Label>
                  <Input
                    value={settings.watermarkContact}
                    onChange={(e) => 
                      setSettings(s => ({ ...s, watermarkContact: e.target.value }))
                    }
                    placeholder="Contact number"
                  />
                </div>

                {/* Position */}
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Select
                    value={settings.watermarkPosition}
                    onValueChange={(value) => 
                      setSettings(s => ({ ...s, watermarkPosition: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top-left">Top Left</SelectItem>
                      <SelectItem value="top-right">Top Right</SelectItem>
                      <SelectItem value="bottom-left">Bottom Left</SelectItem>
                      <SelectItem value="bottom-right">Bottom Right</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Opacity */}
                <div className="space-y-2">
                  <Label>Opacity: {settings.watermarkOpacity}%</Label>
                  <Slider
                    value={[settings.watermarkOpacity]}
                    onValueChange={([value]) => 
                      setSettings(s => ({ ...s, watermarkOpacity: value }))
                    }
                    min={10}
                    max={100}
                    step={5}
                  />
                </div>
              </>
            )}

            {/* Max Files */}
            <div className="space-y-2">
              <Label>Max Files: {settings.maxFiles}</Label>
              <Slider
                value={[settings.maxFiles]}
                onValueChange={([value]) => 
                  setSettings(s => ({ ...s, maxFiles: value }))
                }
                min={1}
                max={20}
                step={1}
              />
            </div>

            {/* Info Box */}
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Auto Compression</p>
                  <p className="text-xs mt-1">
                    Images over 1MB are automatically compressed to ~800KB with max 1920px width.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Images
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ProImageUploader
              onUploadComplete={handleUploadComplete}
              maxFiles={settings.maxFiles}
              watermarkEnabled={settings.watermarkEnabled}
              watermarkText={settings.watermarkText}
              watermarkContact={settings.watermarkContact}
              watermarkPosition={settings.watermarkPosition}
              watermarkOpacity={settings.watermarkOpacity}
            />
          </CardContent>
        </Card>
      </div>

      {/* Uploaded Images Preview */}
      {uploadedUrls.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Uploaded Images ({uploadedUrls.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {uploadedUrls.map((url, index) => (
                <div key={index} className="aspect-square rounded-lg overflow-hidden border">
                  <img
                    src={url}
                    alt={`Uploaded ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(uploadedUrls.join('\n'));
                  toast.success('URLs copied to clipboard!');
                }}
              >
                Copy All URLs
              </Button>
              <Button
                variant="outline"
                onClick={() => setUploadedUrls([])}
              >
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="bg-slate-50">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-3">How to Use</h3>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="bg-sky-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
              <span><strong>Drag & Drop</strong> images or click to browse. Supports JPEG, PNG, WEBP up to 20MB each.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-sky-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
              <span><strong>Auto Compression</strong> - Large images (over 1MB) are automatically compressed to ~800KB.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-sky-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
              <span><strong>Drag to Reorder</strong> - Rearrange images by dragging them to desired positions.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-sky-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">4</span>
              <span><strong>Rename</strong> - Click the edit icon to rename any image before upload.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-sky-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">5</span>
              <span><strong>Upload</strong> - Click "Upload" to send all images. Failed uploads will auto-retry 3 times.</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImageUploadDemo;
